import React, { useState, useEffect, useRef } from "react";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";

import { i18n } from "../../translate/i18n";

import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
  },
}));

const emptyRule = { key: "", condition: 1, value: "" };

const FlowBuilderConditionModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);
  const [combinator, setCombinator] = useState("AND");
  const [rules, setRules] = useState([{ ...emptyRule }]);
  const [labels, setLabels] = useState({
    title: "Adicionar condição ao fluxo",
    btn: "Adicionar",
  });

  useEffect(() => {
    if (open === "edit") {
      setLabels({ title: "Editar condição", btn: "Salvar" });
      const nodeData = data?.data || {};
      if (Array.isArray(nodeData.rules) && nodeData.rules.length > 0) {
        setRules(nodeData.rules.map((r) => ({ ...emptyRule, ...r })));
        setCombinator((nodeData.combinator || "AND").toString().toUpperCase());
      } else if (nodeData.key !== undefined) {
        // Formato legado (regra única)
        setRules([
          {
            key: nodeData.key,
            condition: nodeData.condition || 1,
            value: nodeData.value,
          },
        ]);
        setCombinator("AND");
      } else {
        setRules([{ ...emptyRule }]);
        setCombinator("AND");
      }
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({ title: "Adicionar condição ao fluxo", btn: "Adicionar" });
      setRules([{ ...emptyRule }]);
      setCombinator("AND");
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open, data]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleRuleChange = (index, field, value) => {
    setRules((prev) =>
      prev.map((rule, i) =>
        i === index ? { ...rule, [field]: value } : rule
      )
    );
  };

  const addRule = () => setRules((prev) => [...prev, { ...emptyRule }]);

  const removeRule = (index) =>
    setRules((prev) => prev.filter((_, i) => i !== index));

  const handleSave = () => {
    const cleanRules = rules
      .filter((r) => r.key && r.key.trim() !== "")
      .map((r) => ({
        key: r.key,
        condition: Number(r.condition),
        value: r.value,
      }));

    if (cleanRules.length === 0) return;

    const payload = { combinator, rules: cleanRules };

    if (open === "edit") {
      onUpdate({ ...data, data: payload });
    } else if (open === "create") {
      onSave(payload);
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
        <DialogContent dividers>
          <FormControl variant="outlined" margin="dense" fullWidth>
            <InputLabel id="combinator-label">Combinador</InputLabel>
            <Select
              labelId="combinator-label"
              label="Combinador"
              value={combinator}
              onChange={(e) => setCombinator(e.target.value)}
            >
              <MenuItem value="AND">Todas as regras (E / AND)</MenuItem>
              <MenuItem value="OR">Qualquer regra (OU / OR)</MenuItem>
            </Select>
          </FormControl>

          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: "block", margin: "8px 0" }}
          >
            Use variáveis coletadas no fluxo (respostas de "Pergunta") ou nome,
            numero, email.
          </Typography>

          {rules.map((rule, index) => (
            <Grid
              container
              spacing={1}
              alignItems="center"
              key={index}
              style={{ marginBottom: 4 }}
            >
              <Grid item xs={4}>
                <TextField
                  label="Campo/variável"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  value={rule.key}
                  onChange={(e) =>
                    handleRuleChange(index, "key", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <FormControl variant="outlined" margin="dense" fullWidth>
                  <InputLabel id={`op-label-${index}`}>Regra</InputLabel>
                  <Select
                    labelId={`op-label-${index}`}
                    label="Regra"
                    value={rule.condition}
                    onChange={(e) =>
                      handleRuleChange(index, "condition", e.target.value)
                    }
                  >
                    <MenuItem value={1}>{"=="}</MenuItem>
                    <MenuItem value={2}>{">="}</MenuItem>
                    <MenuItem value={3}>{"<="}</MenuItem>
                    <MenuItem value={4}>{"<"}</MenuItem>
                    <MenuItem value={5}>{">"}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Valor"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  value={rule.value}
                  onChange={(e) =>
                    handleRuleChange(index, "value", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={1}>
                <IconButton
                  size="small"
                  disabled={rules.length === 1}
                  onClick={() => removeRule(index)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Button
            startIcon={<AddIcon />}
            color="primary"
            onClick={addRule}
            style={{ marginTop: 8 }}
          >
            Adicionar regra
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            {i18n.t("contactModal.buttons.cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            className={classes.btnWrapper}
            onClick={handleSave}
          >
            {labels.btn}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderConditionModal;
